/**
 * SlideOverPanel — painel deslizante a partir da direita.
 *
 * Substitui modais aninhados em fluxos complexos (anti-pattern UX
 * mencionado no documento de boas praticas). Preserva contexto da pagina
 * por trás, suporta focus trap basico (ESC para fechar), `aria-modal`
 * e `role="dialog"`, e bloqueia o scroll do body enquanto aberto.
 *
 * Props:
 * - `open` (boolean)
 * - `onClose` () => void — chamado ao clicar fora, ESC ou no botao X
 * - `title`, `subtitle` (strings)
 * - `width` (CSS) — padrao `min(90vw, 520px)`
 * - `footer` (ReactNode) — area sticky com botoes de acao
 * - `dismissOnOverlayClick` (boolean) — default true
 *
 * Animação: @keyframes CSS que tocam ao montar/desmontar (ver styles.jsx),
 * NÃO framer-motion, NÃO transição+rAF.
 * Decisão 2026-05-30 (duas iterações) — framer-motion congelava o painel
 * no meio do slide quando o pai re-renderizava durante a animação; e a
 * transição CSS via double-rAF resolvia o congelamento mas tinha o
 * setVisible starvado pelos re-renders pesados do DadosContext, deixando
 * o painel apagado/vazando por centenas de ms. Keyframes CSS tocam
 * sozinhos no mount (sem timing em JS pra ser starvado) e não reiniciam
 * em re-render do pai (styled-components reusa a classe). Detalhes no
 * comentário de styles.jsx.
 *
 * Máquina de estados local:
 * - `mounted`: presença no DOM. Vira true ao abrir; volta a false só
 *   DEPOIS que a animação de saída termina (EXIT_DURATION_MS), para o
 *   slide-out ser visível antes de desmontar.
 * - `closing`: escolhe qual keyframe tocar (slideOut/fadeOut vs
 *   slideIn/fadeIn).
 */
import React, { useEffect, useRef, useState } from "react";
import { FiX } from "react-icons/fi";

import { useThemeToggle } from "../../context/ThemeContext";
import {
  Body,
  CloseButton,
  Footer,
  Header,
  HeaderText,
  Overlay,
  Panel,
  Subtitle,
  Title,
} from "./styles.jsx";

// Deve ser >= à duração da animação CSS do Panel (0.28s em styles.jsx).
// Pequeno buffer para garantir que a animação terminou antes de desmontar.
const EXIT_DURATION_MS = 300;

function SlideOverPanel({
  open,
  onClose,
  title,
  subtitle,
  width,
  footer,
  children,
  dismissOnOverlayClick = true,
  ariaLabelledBy,
}) {
  const panelRef = useRef(null);
  const titleId = ariaLabelledBy || (title ? "slideover-title" : undefined);

  // Com animações off, os keyframes slideOut/fadeOut são zerados pelo CSS —
  // então desmonta na hora (sem o frame congelado de 300ms com overlay visível).
  const { animacoes = true } = useThemeToggle() || {};

  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  // Ref para onClose — o listener ESC le sempre o valor mais recente
  // sem precisar de `onClose` na dep do useEffect de foco/scroll abaixo.
  // (Parenteses 3 fix: antes `onClose` era inline no caller, recriado
  // a cada render do pai. Como ele estava na dep, useEffect re-rodava
  // a cada render do pai, chamando panelRef.focus() e ROUBANDO O FOCO
  // de inputs/textareas dentro do panel — usuaria nao conseguia digitar.)
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Ref para o elemento focado ANTES do panel abrir. Capturado UMA SO
  // vez por abertura — restaurado no fechamento (a11y).
  const previouslyFocusedRef = useRef(null);
  const closeTimerRef = useRef(null);

  // Sincroniza a prop `open` com a máquina mounted/closing.
  useEffect(() => {
    if (open) {
      // Reabriu antes da saída terminar: cancela o desmonte agendado e
      // volta a tocar o slide-in.
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setClosing(false);
      setMounted(true);
      return undefined;
    }

    // Fechando: toca o slide-out e agenda o desmonte após a animação.
    setClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setMounted(false);
      setClosing(false);
      closeTimerRef.current = null;
    }, animacoes ? EXIT_DURATION_MS : 0);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Scroll lock + ESC + foco — ativo enquanto montado.
  useEffect(() => {
    if (!mounted) return undefined;

    previouslyFocusedRef.current = document.activeElement;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKey(event) {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current?.();
      }
    }
    window.addEventListener("keydown", onKey);

    panelRef.current?.focus?.();

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
      if (previouslyFocusedRef.current?.focus) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [mounted]);

  // Limpa o timer de desmonte se o componente sumir no meio.
  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  const handleOverlayClick = (event) => {
    if (!dismissOnOverlayClick) return;
    if (event.target === event.currentTarget) onClose?.();
  };

  if (!mounted) return null;

  return (
    <Overlay onClick={handleOverlayClick} $closing={closing}>
      <Panel
        $width={width}
        $closing={closing}
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {(title || onClose) && (
          <Header>
            <HeaderText>
              {title && <Title id={titleId}>{title}</Title>}
              {subtitle && <Subtitle>{subtitle}</Subtitle>}
            </HeaderText>
            {onClose && (
              <CloseButton
                type="button"
                onClick={onClose}
                aria-label="Fechar painel"
              >
                <FiX aria-hidden="true" />
              </CloseButton>
            )}
          </Header>
        )}
        <Body>{children}</Body>
        {footer && <Footer>{footer}</Footer>}
      </Panel>
    </Overlay>
  );
}

export default SlideOverPanel;
